import { config } from '@config'
import { CriteriaOperationType } from '@models'
import { CentricPricelistValue, ShowroomMenuEnum, SummaryBarData } from '@modules/common'
import { CentricNodeType, OrderType } from '@modules/common/models/enums'
import { SA_CLIENT_ORDER_PRODUCT_SUMMARY } from '@modules/showroom/basket/graphql'
import { SA_CLIENT_ORDER_PRODUCTS } from '@modules/showroom/collection/graphql/getClientOrderProducts'
import { SA_CLIENT_ORDER_PRODUCT_SUMMARY_DETAILS } from '@modules/showroom/collection/graphql/getClientOrderProductSummaryDetails'
import { SA_CLIENT_ORDER_PRODUCT_WITH_DOOR_PRODUCTS } from '@modules/showroom/collection/graphql/getClientOrderProductWithDoorProducts'
import { SA_DOOR_ORDER_PRODUCT_BY_ORIGINAL } from '@modules/showroom/collection/graphql/getDoorOrderProductByOriginal'
import { SA_DOOR_ORDER_PRODUCTS } from '@modules/showroom/collection/graphql/getDoorOrderProducts'
import { SA_DOOR_ORDER_PRODUCT_SUMMARY_DETAILS } from '@modules/showroom/collection/graphql/getDoorOrderProductSummaryDetails'
import { ORDER_PRODUCT_DETAIL } from '@modules/showroom/productDetail/graphql'
import { getProductsByCategories } from '@services/commonServices'
import { storage } from '@services/storageService'
import { wholesaleCollectionService } from '@services/wholesale/wholesaleCollectionService'
import { apolloClient, stores, strings } from '@stores'
import axios from 'axios'
import cloneDeep from 'lodash/cloneDeep'
import find from 'lodash/find'
import forEach from 'lodash/forEach'
import has from 'lodash/has'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import omit from 'lodash/omit'
import uniqBy from 'lodash/uniqBy'
import concat from 'lodash/concat'
import pick from 'lodash/pick'
import { action, computed, observable } from 'mobx'

enum OrderActions {
  basketTogglingInProgress = 'basketTogglingInProgress',
  doorOrderProductBookingInProgress = 'doorOrderProductBookingInProgress',
  globalProductQtyUpdateInProgress = 'globalProductQtyUpdateInProgress',
  addDoorOrderProductInProgress = 'addDoorOrderProductInProgress',
  bookUnbookTogglingInProgress = 'bookUnbookTogglingInProgress'
}
type CategoryHierarchy = {
  activity: string
  childrenMeta: Array<string>
  childrenMetaValues: Array<string>
}

export class ShowRoomCollectionStore {
  productViewType: string

  @observable summaryBarData: SummaryBarData = {
    useValueRollup: true,
    useSizedQtyRollup: true,
    filterPopover: null
  }

  @observable orderStatus = undefined
  @observable productActionMap: any = {}
  @observable buyingSessionList: Array<{ Id: string; Status: string }> = []
  @observable isBackdropOnLoading: boolean = false
  @observable isDetailsLoading = false
  @observable selectedHeaderFilter =
    strings.showroomHeaders.showroomSubHeaderFilterValues.allProducts
  @observable productDetails = null

  @action onProductDetailClick = productDetails => e => {
    const {
      nav: { updateQueryParams }
    } = stores
    this.setDetailsLoading(true)
    if (!e.shiftKey) {
      const buyingSessionProduct =
        productDetails.node && productDetails.node.BuyingSessionProduct
          ? {
              ...productDetails.node.BuyingSessionProduct,
              Original: productDetails.node?.Original ?? {}
            }
          : productDetails.node
      // don't init half data
      // Object.assign(this, {
      //   productDetails: {
      //     buyingSessionProduct,
      //     isRecommendedProduct: productDetails.isRecommendedProduct,
      //     orderProductId: productDetails.orderProductId,
      //     productAttributes: productDetails.productAttributes
      //   }
      // })
      updateQueryParams({ bspId: buyingSessionProduct.Id }, 'push')
    }
  }

  invalidateCacheForAllOrders = () => {
    // Delete row level queries from cache
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'getVIPDoorOrder',
      broadcast: false
    })
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'getVIPClientOrder',
      broadcast: false
    })
    apolloClient.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'findVIPOrderProduct',
      broadcast: false
    })

    apolloClient.cache.gc()
  }

  @action setDetailsLoading = isDetailsLoading =>
    (this.isDetailsLoading = isDetailsLoading)

  @action setBackDropOnLoading = isLoading => (this.isBackdropOnLoading = isLoading)

  @computed get isDoorOrder() {
    const {
      nav: {
        queryParams: { orderType }
      }
    } = stores
    return orderType === OrderType.Door
  }

  invalidateOrderPageCache = () => {
    const {
      nav: {
        queryParams: { orderId }
      }
    } = stores
    /**
     * Removing only main wrapper query whose data is used when client is master / basket page
     * Cache for all other queries remain up to date
     */
    apolloClient.cache.evict({
      fieldName: 'findVIPOrderProduct',
      args: {
        where: {
          c8__Parent__: {
            [CentricNodeType.VIPClientOrder]: {
              Id: { operation: CriteriaOperationType.EQ, value: orderId }
            }
          }
        }
      }
    })

    // Call garbage collector so that unreachable cache entries are deleted
    apolloClient.cache.gc()
  }

  @action setProductActionTracker = (
    productId: string,
    action: OrderActions,
    status: boolean,
    orderId: string = null
  ) => {
    if (orderId) {
      this.productActionMap[`${productId}-${orderId}`] =
        this.productActionMap[`${productId}-${orderId}`] || {}
      this.productActionMap[`${productId}-${orderId}`][action] = status
    } else {
      this.productActionMap[productId] = this.productActionMap[productId] || {}
      this.productActionMap[productId][action] = status
    }
    this.productActionMap = Object.assign({}, this.productActionMap)
  }

  /**
   * Returns where fragment for door order
   * product
   */
  @computed get doorOrderProductWhereFragment() {
    const {
      nav: {
        queryParams: { orderId }
      }
    } = stores
    if (isEmpty(orderId)) return null
    if (this.isDoorOrder) {
      return {
        c8__Parent__: {
          VIPDoorOrder: {
            Id: { operation: CriteriaOperationType.EQ, value: orderId }
          }
        }
      }
    } else {
      return {
        c8__Parent__: {
          VIPDoorOrder: {
            GIV_VIPDoorOrder_ClientOrder_ref: {
              Id: { operation: CriteriaOperationType.EQ, value: orderId }
            }
          }
        }
      }
    }
  }

  /**
   * Returns where fragment for client order
   * product
   */
  @computed get clientOrderProductWhereFragment() {
    const {
      nav: {
        queryParams: { orderId }
      }
    } = stores
    if (isEmpty(orderId)) return null
    return {
      c8__Parent__: {
        [CentricNodeType.VIPClientOrder]: {
          Id: { operation: CriteriaOperationType.EQ, value: orderId }
        }
      }
    }
  }

  /**
   * Method for building where input for order products query for current client order
   * @param {string} activity - Name of activity
   * @param {Array<string>} childrenMeta - ChildrenMeta for given activity
   * @param {Array<string>} childrenMetaValues - Values for hierarchy children
   * @param {boolean} isClientOrderProducts - To decide whether to return client or door products
   * @returns Where input object for buying session products query for given group
   */
  orderProductsWhere = (
    activity: string,
    childrenMeta: Array<string>,
    childrenMetaValues: Array<string>,
    isClientOrderProducts: boolean = false
  ) => {
    const doorOrderProductWhere = this.doorOrderProductWhereFragment
    const clientOrderProductWhere = this.clientOrderProductWhereFragment
    if (isEmpty(doorOrderProductWhere) || isEmpty(clientOrderProductWhere)) return null

    let baseInputForQuery: any = {
      AND: [
        cloneDeep(isClientOrderProducts ? clientOrderProductWhere : doorOrderProductWhere)
      ]
    }
    baseInputForQuery.AND.push({
      Original: wholesaleCollectionService.buildAttributeHierarchyQuery(
        activity,
        childrenMeta,
        childrenMetaValues
      )
    })

    return baseInputForQuery
  }

  @action setOrderProductIdInProductDetails = async () => {
    const {
      nav: {
        queryParams: { bspId }
      },
      showroomProductDetailStore: {
        whereInputForOrderProduct,
        setProcessedProduct,
        processedProduct
      }
    } = stores
    let { data: orderProductData }: any = await apolloClient.query({
      query: ORDER_PRODUCT_DETAIL,
      fetchPolicy: 'network-only',
      variables: {
        where: whereInputForOrderProduct()
      }
    })
    const orderProduct = orderProductData?.findVIPOrderProduct?.Items?.[0]
    if (
      orderProduct &&
      this.productDetails &&
      this.productDetails?.buyingSessionProduct?.Id === bspId
    ) {
      this.productDetails.orderProductId = orderProduct.Id
    }
    // update new detail store
    if (orderProduct && processedProduct) {
      const _processedProduct = cloneDeep(processedProduct)
      _processedProduct.orderProduct = orderProduct
      setProcessedProduct(_processedProduct)
    }
    //
  }

  updateClientOrderSummaryQueryCacheOnAddingProductToClientOrder = (
    clientOrderProduct,
    orderId: string
  ) => {
    const isShowroomFlow = window.location.pathname.includes('/showroom')
    if (!isShowroomFlow) {
      try {
        // update client order products
        let clientOrderProductSummaryData = apolloClient.readQuery({
          query: SA_CLIENT_ORDER_PRODUCT_SUMMARY,
          variables: {
            where: {
              c8__Parent__: {
                [CentricNodeType.VIPClientOrder]: {
                  Id: { operation: CriteriaOperationType.EQ, value: orderId }
                }
              }
            }
          }
        })

        const clientOrderProductsSummary = cloneDeep(clientOrderProductSummaryData)
        if (clientOrderProductsSummary?.clientOrderProducts) {
          const productToBeAdded = omit(clientOrderProduct, [
            'doorProducts',
            'CreatedAt',
            'isBooked',
            'rtlPrice',
            'whlPrice'
          ])

          if (clientOrderProductsSummary.clientOrderProducts.Items) {
            clientOrderProductsSummary.clientOrderProducts.Items.push(productToBeAdded)
          } else {
            clientOrderProductsSummary.clientOrderProducts.Items = [productToBeAdded]
          }

          apolloClient.writeQuery({
            query: SA_CLIENT_ORDER_PRODUCT_SUMMARY,
            variables: {
              where: {
                c8__Parent__: {
                  [CentricNodeType.VIPClientOrder]: {
                    Id: { operation: CriteriaOperationType.EQ, value: orderId }
                  }
                }
              }
            },
            data: clientOrderProductsSummary
          })
        }
      } catch (err) {}
    }

    try {
      // Update order summary
      let clientOrderSummaryData = apolloClient.readQuery({
        query: SA_CLIENT_ORDER_PRODUCT_SUMMARY_DETAILS,
        variables: {
          orderId
        }
      })

      let clientOrderSummary = cloneDeep(clientOrderSummaryData)
      if (
        clientOrderSummary?.orderProductSummary &&
        clientOrderSummary.orderProductSummary.DoorOrders
      ) {
        forEach(clientOrderSummary.orderProductSummary.DoorOrders, doorOrder => {
          let doorOrderProduct = find(
            clientOrderProduct.doorProducts,
            doorProduct => doorProduct?.DoorOrder.Id === doorOrder.Id
          )

          const doorProduct = {
            ...omit(doorOrderProduct, ['rtlPrice', 'BuyingSessionProduct', 'DoorOrder']),
            Original: {
              Id: doorOrderProduct?.Original?.Id,
              Active: doorOrderProduct?.Original?.Active,
              Activity: doorOrderProduct?.Original?.Activity,
              Family: doorOrderProduct?.Original?.Family,
              Line: doorOrderProduct?.Original?.Line,
              __typename: doorOrderProduct?.Original?.__typename
            },
            clientOrderProduct: pick(clientOrderProduct, [
              'Id',
              'isRecommended',
              'whlPrice',
              '__typename'
            ]),
            __typename: CentricNodeType.VIPOrderProduct
          }

          if (doorProduct) {
            if (doorOrder.OrderLineItems) {
              doorOrder.OrderLineItems.push(doorProduct)
            } else {
              doorOrder.OrderLineItems = [doorProduct]
            }
          }
        })
      }

      apolloClient.writeQuery({
        query: SA_CLIENT_ORDER_PRODUCT_SUMMARY_DETAILS,
        variables: {
          orderId
        },
        data: clientOrderSummary
      })
    } catch (err) {}
  }

  updateDoorOrderProductsQueryOnAddingProductToOrder = (
    newDoorOrderProducts,
    hierarchy: CategoryHierarchy,
    clientOrderProduct?: {
      Id: string
      isRecommended: boolean
      rtlPrice: CentricPricelistValue
      whlPrice: CentricPricelistValue
      __typename: string
    }
  ) => {
    const {
      showroomCollection: { orderProductsWhere }
    } = stores

    try {
      let doorOrderProductsData = apolloClient.readQuery({
        query: SA_DOOR_ORDER_PRODUCTS,
        variables: {
          where: orderProductsWhere(
            hierarchy.activity,
            hierarchy.childrenMeta,
            hierarchy.childrenMetaValues
          )
        }
      })

      let doorOrderProductDataClone = cloneDeep(doorOrderProductsData)
      let doorOrderProductsToBeConcatenated = map(newDoorOrderProducts, orderProduct => {
        let doorOrderProduct = { ...orderProduct }
        doorOrderProduct.__typename = CentricNodeType.VIPOrderProduct
        if (clientOrderProduct) {
          doorOrderProduct.clientOrderProduct = clientOrderProduct
        }

        return doorOrderProduct
      })

      // Concatenate door order products
      if (doorOrderProductDataClone.doorOrderProducts) {
        doorOrderProductDataClone.doorOrderProducts.Items = uniqBy(
          concat(
            doorOrderProductDataClone.doorOrderProducts.Items || [],
            doorOrderProductsToBeConcatenated
          ),
          'Id'
        )

        apolloClient.writeQuery({
          query: SA_DOOR_ORDER_PRODUCTS,
          variables: {
            where: orderProductsWhere(
              hierarchy.activity,
              hierarchy.childrenMeta,
              hierarchy.childrenMetaValues
            )
          },
          data: doorOrderProductDataClone
        })
      }
    } catch (err) {}
  }

  updateCacheOnAddingProductToClientOrder = async (
    node: any,
    productId: string,
    orderId: string
  ) => {
    const { activity, childrenMeta, childrenMetaValues } = getProductsByCategories(node)
    let {
      data: { findVIPOrderProduct }
    } = await apolloClient.query({
      query: SA_CLIENT_ORDER_PRODUCT_WITH_DOOR_PRODUCTS,
      variables: {
        seasonalProduct: productId,
        clientOrderId: orderId
      },
      fetchPolicy: 'network-only'
    })

    if (findVIPOrderProduct.Items.length) {
      const clientOrderProduct = cloneDeep(findVIPOrderProduct.Items[0])
      clientOrderProduct.__typename = CentricNodeType.VIPOrderProduct
      const withVIPProductDetails = ShowroomMenuEnum.basket === this.productViewType

      try {
        // Read data to be updated from cache
        let clientOrderProductsData = apolloClient.readQuery({
          query: SA_CLIENT_ORDER_PRODUCTS,
          variables: {
            where: this.orderProductsWhere(
              activity,
              childrenMeta,
              childrenMetaValues,
              true
            ),
            withVIPProductDetails
          }
        })

        const clientOrderItems = cloneDeep(clientOrderProductsData)
        let clientOrderProductNode = {
          ...clientOrderProduct,
          ...(withVIPProductDetails
            ? { Original: clientOrderProduct?.Original }
            : {
                Original: {
                  Id: clientOrderProduct?.Original?.Id,
                  Name: clientOrderProduct?.Original?.Name,
                  __typename: clientOrderProduct?.Original?.__typename
                }
              }),
          rtlPrice: clientOrderProduct.rtlPrice
            ? omit(clientOrderProduct.rtlPrice, 'Code')
            : null,
          whlPrice: clientOrderProduct.whlPrice
            ? omit(clientOrderProduct.whlPrice, 'Code')
            : null
        }

        if (clientOrderItems?.clientOrderProducts?.Items?.length) {
          clientOrderItems?.clientOrderProducts?.Items.push(clientOrderProductNode)
        } else if (clientOrderItems.clientOrderProducts) {
          clientOrderItems.clientOrderProducts.Items = [clientOrderProductNode]
        }

        apolloClient.writeQuery({
          query: SA_CLIENT_ORDER_PRODUCTS,
          variables: {
            where: this.orderProductsWhere(
              activity,
              childrenMeta,
              childrenMetaValues,
              true
            ),
            withVIPProductDetails
          },
          data: clientOrderItems
        })
      } catch (err) {}

      this.updateClientOrderSummaryQueryCacheOnAddingProductToClientOrder(
        clientOrderProduct,
        orderId
      )

      this.updateDoorOrderProductsQueryOnAddingProductToOrder(
        clientOrderProduct.doorProducts,
        { activity, childrenMeta, childrenMetaValues },
        pick(clientOrderProduct, [
          'Id',
          'isRecommended',
          'rtlPrice',
          'whlPrice',
          '__typename'
        ])
      )
    }
  }

  updateDoorOrderSummaryQueryCacheOnAddingProductToDoorOrder = (
    doorOrderProduct,
    doorOrderId: string
  ) => {
    try {
      let doorOrderSummaryData = apolloClient.readQuery({
        query: this.isDoorOrder
          ? SA_DOOR_ORDER_PRODUCT_SUMMARY_DETAILS
          : SA_CLIENT_ORDER_PRODUCT_SUMMARY_DETAILS,
        variables: {
          orderId: stores?.nav?.queryParams?.orderId
        }
      })
      let orderSummary = cloneDeep(doorOrderSummaryData)

      if (orderSummary?.orderProductSummary) {
        const updatedDoorOrder = has(orderSummary.orderProductSummary, 'DoorOrders')
          ? find(
              orderSummary.orderProductSummary?.DoorOrders,
              doorOrder => doorOrder?.Id === doorOrderId
            )
          : orderSummary.orderProductSummary

        const doorProduct: any = this.isDoorOrder
          ? {
              ...omit(doorOrderProduct, ['rtlPrice', 'DoorOrder']),
              Original: omit(doorOrderProduct?.Original, [
                'DefaultImage',
                'Name',
                'Sizes'
              ]),
              clientOrderProduct: omit(doorOrderProduct?.clientOrderProduct, ['rtlPrice'])
            }
          : {
              ...omit(doorOrderProduct, [
                'rtlPrice',
                'BuyingSessionProduct',
                'DoorOrder'
              ]),
              Original: {
                Id: doorOrderProduct?.Original?.Id,
                Active: doorOrderProduct?.Original?.Active,
                Activity: doorOrderProduct?.Original?.Activity,
                Family: doorOrderProduct?.Original?.Family,
                Line: doorOrderProduct?.Original?.Line,
                __typename: doorOrderProduct?.Original?.__typename
              },
              clientOrderProduct: omit(doorOrderProduct?.clientOrderProduct, ['rtlPrice'])
            }
        doorProduct.__typename = CentricNodeType.VIPOrderProduct
        if (updatedDoorOrder.OrderLineItems) {
          if (
            !find(updatedDoorOrder.OrderLineItems, item => item.Id === doorProduct.Id)
          ) {
            updatedDoorOrder.OrderLineItems.push(doorProduct)
          }
        } else {
          updatedDoorOrder.OrderLineItems = [doorProduct]
        }
      }

      apolloClient.writeQuery({
        query: this.isDoorOrder
          ? SA_DOOR_ORDER_PRODUCT_SUMMARY_DETAILS
          : SA_CLIENT_ORDER_PRODUCT_SUMMARY_DETAILS,
        variables: {
          orderId: stores?.nav?.queryParams?.orderId
        },
        data: orderSummary
      })
    } catch (error) {
      /**
       * Apollo client throws exception if query is not present in cache.
       * We might have query missing for products section in case product for new category is added. Silently ignoring it in that
       * case
       */
    }
  }

  updateCacheOnAddingProductToDoorOrder = async (node, productId, orderId) => {
    const { activity, childrenMeta, childrenMetaValues } = getProductsByCategories(node)
    let {
      data: { findVIPOrderProduct }
    } = await apolloClient.query({
      query: SA_DOOR_ORDER_PRODUCT_BY_ORIGINAL,
      variables: {
        seasonalProduct: productId,
        doorOrderId: orderId
      },
      fetchPolicy: 'network-only'
    })

    const doorOrderProduct = findVIPOrderProduct?.Items?.[0]

    if (doorOrderProduct) {
      this.updateDoorOrderProductsQueryOnAddingProductToOrder([doorOrderProduct], {
        activity,
        childrenMeta,
        childrenMetaValues
      })

      this.updateDoorOrderSummaryQueryCacheOnAddingProductToDoorOrder(
        doorOrderProduct,
        orderId
      )
    }
  }

  @action
  addProductToBasket = async (node, productId, orderId, bspId?) => {
    const SA_ADD_PRODUCT_TO_DOOR_ORDER_URL = `${config.centricRestEndpoint}addProductToDoor`
    const ADD_PRODUCT_TO_CLIENT_ORDER_URL = `${config.centricRestEndpoint}addProductToClient`

    try {
      this.setProductActionTracker(productId, OrderActions.basketTogglingInProgress, true)

      const URL = this.isDoorOrder
        ? SA_ADD_PRODUCT_TO_DOOR_ORDER_URL
        : ADD_PRODUCT_TO_CLIENT_ORDER_URL
      const sessionURL: string = storage.getItem('session_url')
      const body = {
        ...(this.isDoorOrder
          ? {
              doorOrder: orderId
            }
          : {
              clientOrder: orderId
            }),
        product: productId
      }

      // api call
      const { data } = await axios.post(URL, body, {
        headers: {
          Authorization: `Bearer ${sessionURL}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (data?.status === strings.mutationResultSuccess) {
        const {
          orderManagementStore: { addProductsAddedToOrder }
        } = stores

        addProductsAddedToOrder(productId)

        // In case request is made from product details view, update product details saved in store
        if (bspId) {
          this.setOrderProductIdInProductDetails()
        }

        // Update cache
        this.isDoorOrder
          ? await this.updateCacheOnAddingProductToDoorOrder(node, productId, orderId)
          : await this.updateCacheOnAddingProductToClientOrder(node, productId, orderId)
      }
    } finally {
      this.setProductActionTracker(
        productId,
        OrderActions.basketTogglingInProgress,
        false
      )
    }
  }
}

export const showroomCollection = new ShowRoomCollectionStore()
